<template>
  <Teleport to="body">
    <Transition name="mobile-menu">
      <div v-if="isOpen" class="mobile-menu" @click.self="handleOverlayClick">
        <!-- 遮罩层 -->
        <div class="mobile-menu__overlay"></div>

        <!-- 侧滑面板 -->
        <div class="mobile-menu__panel">
          <!-- 关闭按钮 -->
          <button class="mobile-menu__close" @click="emit('close')" aria-label="关闭菜单">
            <span></span>
            <span></span>
          </button>

          <!-- 导航链接 -->
          <nav class="mobile-menu__nav">
            <router-link
              v-for="item in navItems"
              :key="item.path"
              :to="item.path"
              class="mobile-menu__link"
              @click="emit('close')"
            >
              {{ item.label }}
            </router-link>
          </nav>

          <!-- 底部操作区 -->
          <div class="mobile-menu__footer">
            <div class="mobile-menu__lang-switch">
              <button
                class="lang-btn"
                :class="{ 'lang-btn--active': currentLang === 'zh-CN' }"
                @click="setLang('zh-CN')"
              >简</button>
              <button
                class="lang-btn"
                :class="{ 'lang-btn--active': currentLang === 'zh-TW' }"
                @click="setLang('zh-TW')"
              >繁</button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, inject } from 'vue'
import { state } from '@/i18n'

defineProps({
  isOpen: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close'])

const t = inject('t')

const currentLang = computed(() => state.currentLocale)

function setLang(lang) {
  state.currentLocale = lang
}

const navItems = [
  { path: '/', labelKey: 'nav.home' },
  { path: '/about', labelKey: 'nav.about' },
  { path: '/strategy', labelKey: 'nav.strategy' },
  { path: '/risk-compliance', labelKey: 'nav.riskCompliance' },
  { path: '/cross-border', labelKey: 'nav.crossBorder' },
  { path: '/contact', labelKey: 'nav.contact' },
]

// 使用 computed 风格的响应式标签
navItems.forEach(item => {
  Object.defineProperty(item, 'label', {
    get() { return t(item.labelKey) }
  })
})

function handleOverlayClick() {
  emit('close')
}
</script>

<style scoped>
.mobile-menu {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: var(--z-mobile-menu);
  display: flex;
}

/* 遮罩层 */
.mobile-menu__overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

/* 侧滑面板 */
.mobile-menu__panel {
  position: relative;
  margin-left: auto;
  width: 300px;
  max-width: 80vw;
  height: 100%;
  background: #ffffff;
  border-left: 1px solid var(--color-border-light);
  display: flex;
  flex-direction: column;
  padding: var(--space-8) var(--space-6);
  overflow-y: auto;
}

/* 关闭按钮（X 图标） */
.mobile-menu__close {
  position: absolute;
  top: var(--space-5);
  right: var(--space-5);
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  transition: opacity var(--transition-fast);
}

.mobile-menu__close:hover {
  opacity: 0.7;
}

.mobile-menu__close span {
  display: block;
  width: 18px;
  height: 2px;
  background: var(--color-text-primary);
  border-radius: 1px;
  transition: transform var(--transition-base);
}

.mobile-menu__close span:first-child {
  transform: rotate(45deg) translate(4px, 4px);
}

.mobile-menu__close span:last-child {
  transform: rotate(-45deg) translate(4px, -4px);
}

/* 导航链接 */
.mobile-menu__nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  margin-top: var(--space-12);
}

.mobile-menu__link {
  display: block;
  padding: var(--space-4) var(--space-4);
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: 400;
  color: var(--color-text-secondary);
  text-decoration: none;
  letter-spacing: 0.03em;
  border-bottom: 1px solid transparent;
  transition: all var(--transition-fast);
  border-radius: var(--radius-sm);
}

.mobile-menu__link:hover,
.mobile-menu__link.router-link-active,
.mobile-menu__link.router-link-exact-active {
  color: var(--blue);
  background: var(--blue-soft);
  border-bottom-color: rgba(25, 118, 210, 0.15);
  padding-left: var(--space-6);
}

/* 底部操作区 */
.mobile-menu__footer {
  margin-top: auto;
  padding-top: var(--space-8);
  border-top: 1px solid var(--color-divider);
}

/* 语言切换按钮组 */
.mobile-menu__lang-switch {
  display: flex;
  gap: 8px;
}

.mobile-menu__lang-switch .lang-btn {
  flex: 1;
  padding: var(--space-3) var(--space-6);
  font-family: var(--font-sans);
  font-size: var(--text-sm);
  font-weight: 500;
  letter-spacing: 0.08em;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: var(--surface);
  color: var(--text-muted);
  cursor: pointer;
  text-align: center;
  transition: all var(--transition-fast);
}

.mobile-menu__lang-switch .lang-btn:hover {
  border-color: var(--blue-light);
  color: var(--blue);
}

.mobile-menu__lang-switch .lang-btn--active {
  border-color: var(--blue);
  background: var(--blue-soft);
  color: var(--blue);
}

/* ====== 动画 ====== */

/* 面板从右侧滑入 */
.mobile-menu-enter-active .mobile-menu__panel {
  transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
}
.mobile-menu-leave-active .mobile-menu__panel {
  transition: transform 0.25s cubic-bezier(0.22, 1, 0.36, 1);
}
.mobile-menu-enter-from .mobile-menu__panel,
.mobile-menu-leave-to .mobile-menu__panel {
  transform: translateX(100%);
}

/* 遮罩层淡入淡出 */
.mobile-menu-enter-active .mobile-menu__overlay {
  transition: opacity 0.3s ease;
}
.mobile-menu-leave-active .mobile-menu__overlay {
  transition: opacity 0.2s ease;
}
.mobile-menu-enter-from .mobile-menu__overlay,
.mobile-menu-leave-to .mobile-menu__overlay {
  opacity: 0;
}
</style>
